import React from 'react';
import { Dealer } from '../../../../domains/Dealer';
import styled from 'styled-components';
import { ChevronDownIc } from '@dsch/dd-icons';
import { Link } from '../../../../components/Toolkit/Button/Link';
import { ButtonLink } from '../../../../components/Toolkit/Button/ButtonLink';
import { useDealerContactDetails } from '../../../../components/DealerPage/components/DealerContactDetails/DealerContactDetails.hook';
import { DealerHours } from '../../../Toolkit/DealerHours/DealerHours';

interface DealerContactDetailsProps {
  dealer: Dealer;
  cdnUrl: string;
  domain: string;
}

export const List = styled.ul`
  flex: 1 1 auto;
  padding: ${({ theme }) => theme.spacing.M16};
  margin-top: ${({ theme }) => theme.spacing.S8};
  margin-bottom: ${({ theme }) => theme.spacing.S8};
`;

export const ListItem = styled.li<{ isOpen: boolean }>`
  ${({ theme }) => theme.fontSize.M14};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  word-break: break-word;
  display: flex;
  justify-content: space-between;
  .day {
    color: ${({ theme, isOpen }) =>
      isOpen ? theme.colors.GREEN : theme.colors.GREY_DARK};
  }
  .hours {
    color: ${({ theme, isOpen }) =>
      isOpen ? theme.colors.GREEN : theme.colors.GREY_DARKER};
  }
`;

export const ListItemOpen = styled.span<{ isOpen: boolean }>`
  color: ${({ theme, isOpen }) =>
    isOpen ? theme.colors.GREEN : theme.colors.RED_DARK};
`;

export const ListItemOpenHours = styled.span<{ isOpen: boolean }>`
  color: ${({ theme, isOpen }) =>
    isOpen ? theme.colors.GREEN : theme.colors.GREY_DARKER};
`;

export const ListItemTitle = styled.span<{ isBold: boolean }>`
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  font-weight: ${({ theme, isBold }) =>
    isBold ? theme.fontWeight.bold : theme.fontWeight.regular};
`;

export const SDealerContactDetails = styled.div`
  display: flex;
  ${({ theme }) => theme.fontSize.S12};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  word-break: break-word;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.S8};
  border-top: 1px solid ${({ theme }) => theme.colors.GREY_LIGHT};
  padding-top: ${({ theme }) => theme.spacing.M24};
  margin-top: ${({ theme }) => theme.spacing.M16};
`;

export const SDealerContactStatus = styled.div<{ isOpen: boolean }>`
  display: flex;
  ${({ theme }) => theme.fontSize.M14};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  word-break: break-word;
  justify-content: space-between;
  cursor: pointer;
`;

export const SDealerContactLocationContainer = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.S8};
`;

export const SDealerContactLocation = styled.div`
  display: flex;
  ${({ theme }) => theme.fontSize.M14};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  word-break: break-word;
  justify-content: space-between;
  cursor: pointer;
  span:first-child {
    flex: 0 0 auto;
    max-width: 100%;
  }
  span:last-child {
    flex: 1 1 0px;
    padding-left: ${({ theme }) => theme.spacing.S8};
    align-items: center;
    justify-content: end;
    align-content: end;
    display: flex;
    text-align: right;
  }
`;

export const SDealerContactLocationGoogleMapsLink = styled.div`
  ${({ theme }) => theme.fontSize.M14};
  a {
    text-transform: none;
    text-align: right;
    padding-top: ${({ theme }) => theme.spacing.M16};
    ${({ theme }) => theme.fontSize.S12};
    font-weight: ${({ theme }) => theme.fontWeight.regular};
  }
`;

interface IChevron {
  $isMenuOpen: boolean;
  $isDisabled: boolean;
}

export const DealerChevron = styled(ChevronDownIc)<IChevron>`
  margin-left: ${({ theme }) => theme.spacing.S4};
  color: ${({ theme }) => theme.colors.GREY_DARKER};
  transform: rotate(${({ $isMenuOpen }) => ($isMenuOpen ? '180deg' : '0')});
`;

const DealerContactDetails = ({ dealer }: DealerContactDetailsProps) => {
  const {
    dealerPhone,
    setLocationOpen,
    isLocationOpen,
    handleShowPhoneNumber,
    isSubmitting,
  } = useDealerContactDetails(dealer);

  return (
    <SDealerContactDetails>
      <DealerHours openingHours={dealer.workingHours} />
      <SDealerContactLocationContainer>
        <SDealerContactLocation
          onClick={() => setLocationOpen(!isLocationOpen)}
        >
          <ListItemTitle isBold={false}>{'Location'}</ListItemTitle>
          <ListItemTitle isBold={true}>
            {dealer.address}
            <DealerChevron
              $isMenuOpen={isLocationOpen}
              height={16}
              width={16}
              $isDisabled={false}
            />
          </ListItemTitle>
        </SDealerContactLocation>
        {isLocationOpen && (
          <SDealerContactLocationGoogleMapsLink>
            <Link target={'_blank'} href={dealer.googleMapsLink}>
              {dealer.fullAddress}
            </Link>
          </SDealerContactLocationGoogleMapsLink>
        )}
      </SDealerContactLocationContainer>

      <ButtonLink
        ofType="SECONDARY"
        data-testid={dealerPhone ? 'call-phone-number' : 'view-phone-number'}
        href={dealerPhone ? `tel:${dealerPhone}` : undefined}
        onClick={handleShowPhoneNumber}
      >
        {dealerPhone && !isSubmitting
          ? `Call ${dealerPhone}`
          : isSubmitting
          ? 'Loading'
          : 'Show Phone Number'}
      </ButtonLink>
    </SDealerContactDetails>
  );
};

export { DealerContactDetails };
